// Copyright 2016 Sorama B.V.

import { BinaryData, Data, DecimalData } from "units/Data";
import { Frequency } from "units/Frequency";
import { Length } from "units/Length";
import { Pressure } from "units/Pressure";
import { Time } from "units/Time";
import { Unit, UnitType } from "units/Unit";
import { AcousticIntensity } from "./AcousticIntensity";
import { BarkBand } from "./BarkBand";
import { CompositeUnit } from "./CompositeUnit";
import { CriticalBand } from "./CriticalBand";
import { Currency } from "./Currency";
import { Energy, EnergyCost, EnergyUsage } from "./Energy";
import { Mass, MassCost } from "./Mass";
import { MassFlow } from "./MassFlow";
import { Power, PowerCost, PowerRate } from "./Power";
import { FluctuationStrength, Loudness, Roughness, Sharpness, Tonality } from "./PsychoAcousticsUnits";
import { Velocity } from "./Velocity";
import { Volume, VolumeCost } from "./Volume";
import { FlowEffort, VolumeFlow } from "./VolumeFlow";

export class UnitParser {
    private static allUnitTypes: UnitType[] = [
        Time,
        Length,
        Frequency,
        Pressure,
        Data,
        DecimalData,
        BinaryData,
        Velocity,
        AcousticIntensity,
        Mass,
        MassCost,
        Volume,
        VolumeCost,
        Energy,
        EnergyCost,
        EnergyUsage,
        VolumeFlow,
        MassFlow,
        FlowEffort,
        Currency,
        Power,
        PowerCost,
        PowerRate,
        CriticalBand,
        BarkBand,
        Loudness,
        Sharpness,
        Roughness,
        FluctuationStrength,
        Tonality,
    ];

    public static Parse(value: string): Unit {
        if (!value || typeof value !== "string") return undefined;
        for (const unitType of UnitParser.allUnitTypes) {
            const props = Object.keys(unitType);
            let unit = UnitParser.matchProperty(value, props, unitType);

            if (unit) return unit;
        }

        // If still not found, try to match composite units that are not defined explicitly
        return UnitParser.parseCompositeUnit(value);
    }

    private static parseCompositeUnit(value: string): Unit {
        // Use a regex to find left and right of / (or per)
        const compositeUnitRegex = /^(.*?)(?:\/|per)(.*?)$/i;
        const match = value.match(compositeUnitRegex);
        if (match) {
            const leftUnit = UnitParser.Parse(match[1].trim());
            const rightUnit = UnitParser.Parse(match[2].trim());
            if (leftUnit && rightUnit) {
                // Find existing composite unit that is made up of the same two units
                let otherUnit: Unit = null;
                let otherUnitType: typeof UnitType = null;

                for (const unitType of UnitParser.allUnitTypes) {
                    const props = Object.keys(unitType);
                    for (const prop of props) {
                        const unit = unitType[prop];
                        if (
                            unit instanceof CompositeUnit &&
                            unit.numerator.unit === leftUnit.unit &&
                            unit.denominator.unit === rightUnit.unit
                        ) {
                            otherUnit = unit;
                            otherUnitType = unitType as typeof UnitType;
                            break;
                        }
                    }
                    if (otherUnit) break;
                }

                // If found, use family of the other unit
                if (otherUnit && otherUnitType) {
                    return new CompositeUnit(leftUnit, rightUnit, otherUnitType, {
                        aliases: [value],
                    });
                }

                // If not found, create a new CompositeUnit with a generic unit type
                return new CompositeUnit(leftUnit, rightUnit, UnitType, {
                    aliases: [value],
                });
            }
        }

        return undefined;
    }

    private static matchProperty(value: string, propertyNames: string[], containerClass: unknown): Unit {
        const formattedValue = UnitParser.formatUnitString(value);
        for (let i = 0; i < propertyNames.length; i++) {
            const property = containerClass[propertyNames[i]];

            // Check the variable name of the property
            const formattedVariableName = UnitParser.formatUnitString(propertyNames[i]);
            if (formattedValue === formattedVariableName) {
                return property;
            }

            // Check the name that is inputted into the unit constructor (Unit.name field)
            if (containerClass[propertyNames[i]].unit) {
                const formattedAlternativeName = UnitParser.formatUnitString(containerClass[propertyNames[i]].unit);
                if (formattedValue == formattedAlternativeName) {
                    return property;
                }
            }

            // Check the aliases
            if (containerClass[propertyNames[i]].aliases) {
                for (const alias of containerClass[propertyNames[i]].aliases) {
                    const formattedAlias = UnitParser.formatUnitString(alias);
                    if (formattedValue == formattedAlias) {
                        return property;
                    }
                }
            }
        }

        return undefined;
    }

    // Tolowercase and remove spaces
    private static formatUnitString(value: string): string {
        return value.toLowerCase().replace(/\s/g, "");
    }
}
